import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const normalizeReading = (rdg) => ({
    ...rdg,
    fileUid: rdg.file?.uid ?? null,
});

/**
 * Fetch ALL readings across all suppliers and services.
 * Walks suppliers → services → readings, enriching each reading
 * with supplierUid, supplierName, serviceUid, serviceName, and serviceType.
 */
export const useAllReadings = () => {
    return useQuery({
        queryKey: ['all-readings'],
        queryFn: async () => {
            const suppliers = await api.suppliers();
            const results = await Promise.all(
                suppliers.map(async (sup) => {
                    const services = await api.getServicesBySupplier(sup.uid);
                    const readingsByService = await Promise.all(
                        services.map(async (svc) => {
                            const readings = await api.getReadingsByService(svc.uid);
                            return readings.map((rdg) => normalizeReading({
                                ...rdg,
                                supplierUid: sup.uid,
                                supplierName: sup.name,
                                serviceUid: svc.uid,
                                serviceName: svc.name,
                                serviceType: svc.type,
                            }));
                        })
                    );
                    return readingsByService.flat();
                })
            );
            return results.flat();
        },
    });
};

export const useReadingsByService = (serviceUid) => {
    return useQuery({
        queryKey: ['readings', { serviceUid }],
        queryFn: async () => {
            const readings = await api.getReadingsByService(serviceUid);
            return readings.map((rdg) => normalizeReading({ ...rdg, serviceUid }));
        },
        enabled: !!serviceUid,
    });
};

export const useCreateReading = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceUid, data }) =>
            api.createReading(serviceUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['readings'] });
            queryClient.invalidateQueries({ queryKey: ['all-readings'] });
        },
    });
};

export const useUpdateReading = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ readingUid, data }) =>
            api.updateReading(readingUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['readings'] });
            queryClient.invalidateQueries({ queryKey: ['all-readings'] });
        },
    });
};

export const useDeleteReading = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ readingUid }) =>
            api.deleteReading(readingUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['readings'] });
            queryClient.invalidateQueries({ queryKey: ['all-readings'] });
        },
    });
};
